// ============================================
// VOLUNTEER MODULE - MAIN APP
// Mobile responsive with hamburger menu
// ============================================

import React, { useState, useEffect } from "react";
import { ThemeProvider, useTheme } from "./components/ThemeContext";
import { VolunteerDataProvider } from "./components/VolunteerDataContext";
import { AuthProvider, useAuth, LoginPage, ROLE_COLORS, ROLE_LABELS, ROLES } from "./components/Auth";
import Sidebar from "./components/Sidebar";
import Dashboard from "./pages/Dashboard";
import UsersAdmin from "./pages/UsersAdmin";
import AcceptInvite from "./pages/AcceptInvite";
import ResetPassword from "./pages/ResetPassword";
import Events from "./pages/Events";
import EventDetail from "./pages/EventDetail";
import Volunteers from "./pages/Volunteers";
import DutyAssignment from "./pages/DutyAssignment";
import Attendance from "./pages/Attendance";
import Reports from "./pages/Reports";

function AccessDenied() {
  const { C } = useTheme();
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height:"60vh" }}>
      <div style={{ fontSize:50, marginBottom:16 }}>🔒</div>
      <div style={{ fontSize:18, fontWeight:700, color:C.text, marginBottom:8 }}>Access Denied</div>
      <div style={{ fontSize:13, color:C.muted }}>You don't have permission to view this page.</div>
    </div>
  );
}

function MyDuties({ user }) {
  const { C } = useTheme();
  return (
    <div style={{ maxWidth:560, margin:"0 auto" }}>
      <div style={{ fontSize:20, fontWeight:800, color:C.text, fontFamily:"Georgia, serif", marginBottom:6 }}>My Duties</div>
      <div style={{ fontSize:13, color:C.muted, marginBottom:20 }}>Hello {user.name} 👋</div>
      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:40, textAlign:"center" }}>
        <div style={{ fontSize:36, marginBottom:14 }}>🗂️</div>
        <div style={{ fontSize:14, fontWeight:600, color:C.text, marginBottom:6 }}>No duties assigned yet</div>
        <div style={{ fontSize:13, color:C.muted }}>Check back after the admin completes assignments.</div>
      </div>
    </div>
  );
}

function UserBox({ user, logout }) {
  const { C } = useTheme();
  const [hov, setHov] = useState(false);
  return (
    <div style={{ padding:"14px", borderTop:`1px solid ${C.border}`, background:C.navBg, flexShrink:0, transition:"background 0.3s" }}>
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
        <div style={{ width:32, height:32, borderRadius:"50%", background:ROLE_COLORS[user.role], display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:800, color:"#000", flexShrink:0 }}>
          {user.avatar}
        </div>
        <div style={{ flex:1, overflow:"hidden" }}>
          <div style={{ fontSize:12, color:C.text, fontWeight:700, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{user.name}</div>
          <div style={{ fontSize:10, color:ROLE_COLORS[user.role], fontWeight:700 }}>{ROLE_LABELS[user.role]}</div>
        </div>
      </div>
      <button onClick={logout}
        onMouseEnter={()=>setHov(true)}
        onMouseLeave={()=>setHov(false)}
        style={{ width:"100%", padding:"8px", background:hov?"#FF4D6D22":"transparent", border:`1px solid ${hov?"#FF4D6D":C.border}`, borderRadius:9, color:hov?"#FF4D6D":C.muted, fontSize:12, fontWeight:600, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:7, transition:"all 0.2s" }}>
        <span>🚪</span><span>Sign Out</span>
      </button>
    </div>
  );
}

// ── MOBILE TOP BAR ─────────────────────────
function MobileTopBar({ activePage, onMenuToggle, C }) {
  const PAGE_NAMES = {
    dashboard:   "Dashboard",
    events:      "Events",
    volunteers:  "Volunteers",
    assign:      "Duty Assignment",
    attendance:  "Attendance",
    reports:     "Reports",
    eventDetail: "Event Detail",
  };

  return (
    <div style={{
      display:"flex", alignItems:"center", justifyContent:"space-between",
      padding:"12px 16px",
      background:C.navBg,
      borderBottom:`1px solid ${C.border}`,
      position:"sticky", top:0, zIndex:50,
      transition:"background 0.3s",
    }}>
      {/* Hamburger button */}
      <button onClick={onMenuToggle}
        style={{ background:"none", border:`1px solid ${C.border}`, borderRadius:8, padding:"6px 10px", cursor:"pointer", color:C.text, fontSize:18, display:"flex", alignItems:"center", justifyContent:"center" }}>
        ☰
      </button>

      {/* Page title */}
      <div style={{ fontSize:15, fontWeight:800, color:C.accent }}>
        ⚡ {PAGE_NAMES[activePage] || "VolunteerHub"}
      </div>

      {/* Placeholder for symmetry */}
      <div style={{ width:40 }} />
    </div>
  );
}

function AppInner() {
  const { user, logout, can, loading } = useAuth();
  const { C } = useTheme();

  const [activePage, setActivePage]       = useState("dashboard");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [sidebarOpen, setSidebarOpen]     = useState(false);
  const [isMobile, setIsMobile]           = useState(window.innerWidth < 768);
  // Active event is computed by Dashboard from real data and pushed up here.
  // Sidebar reads from this state — single source of truth across the app.
  const [activeEvent, setActiveEvent] = useState(null);

  // Format dates for sidebar display ("Apr 10–14, 2025")
  const formatActiveEvent = (ev) => {
    if(!ev) return null;
    const fmt = (s) => { try { return new Date(s).toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" }); } catch { return s; } };
    let dates = "";
    if(ev.startDate && ev.endDate && ev.startDate !== ev.endDate) {
      dates = `${fmt(ev.startDate)} – ${fmt(ev.endDate)}`;
    } else if(ev.startDate) {
      dates = fmt(ev.startDate);
    }
    return { id: ev.id, name: ev.name, dates };
  };

  const handleActiveEventChange = (ev) => setActiveEvent(formatActiveEvent(ev));

  // Track screen size
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close sidebar when navigating on mobile
  const handleNavigate = (page) => {
    setSelectedEvent(null);
    setActivePage(page);
    if(isMobile) setSidebarOpen(false);
  };

  const handleViewEvent = (event) => {
    setSelectedEvent(event);
    setActivePage("eventDetail");
    if(isMobile) setSidebarOpen(false);
  };

  const handleBackFromDetail = () => {
    setSelectedEvent(null);
    setActivePage("events");
  };

  // ── PUBLIC ROUTES ─────────────────────────
  // /accept-invite/:token and /reset-password/:token render BEFORE the
  // auth gate so people without a session can use them.
  const path = window.location.pathname;
  if (path.startsWith("/accept-invite/")) {
    const token = path.split("/").filter(Boolean)[1];
    if (token) return <AcceptInvite token={token} />;
  }
  if (path.startsWith("/reset-password/")) {
    const token = path.split("/").filter(Boolean)[1];
    if (token) return <ResetPassword token={token} />;
  }

  // Loading: avoid flashing the login screen while /auth/me is checking
  if (loading) {
    return (
      <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:C.bg, color:C.muted, fontSize:13 }}>
        Loading…
      </div>
    );
  }

  if (!user) return <LoginPage />;

  const renderPage = () => {
    if(activePage === "eventDetail" && selectedEvent) {
      return <EventDetail event={selectedEvent} onBack={handleBackFromDetail} C={C} />;
    }
    switch(activePage) {
      case "dashboard":  return can("view_dashboard")  ? <Dashboard onViewEvent={handleViewEvent} onActiveEventChange={handleActiveEventChange} />  : <AccessDenied />;
      case "events":     return can("create_event")    ? <Events onViewEvent={handleViewEvent} />      : <AccessDenied />;
      case "volunteers": return can("view_volunteers") ? <Volunteers />                                : <AccessDenied />;
      case "assign":     return can("assign_duties")   ? <DutyAssignment />                           : <AccessDenied />;
      case "attendance": return can("view_attendance") ? <Attendance />                               : <AccessDenied />;
      case "reports":    return can("view_reports")    ? <Reports />                                  : <AccessDenied />;
      case "users":      return can("manage_users")    ? <UsersAdmin />                               : <AccessDenied />;
      default:           return <Dashboard onViewEvent={handleViewEvent} onActiveEventChange={handleActiveEventChange} />;
    }
  };

  // ── MOBILE LAYOUT ─────────────────────────
  if(isMobile) {
    return (
      <div style={{ height:"100vh", display:"flex", flexDirection:"column", overflow:"hidden", background:C.bg }}>

        {/* Mobile Top Bar */}
        <MobileTopBar
          activePage={activePage}
          onMenuToggle={() => setSidebarOpen(o => !o)}
          C={C}
        />

        {/* Overlay when sidebar is open */}
        {sidebarOpen && (
          <div
            onClick={() => setSidebarOpen(false)}
            style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:99 }}
          />
        )}

        {/* Slide-in Sidebar */}
        <div style={{
          position:"fixed", top:0, left:0, bottom:0,
          width:240,
          background:C.navBg,
          borderRight:`1px solid ${C.border}`,
          zIndex:100,
          transform: sidebarOpen ? "translateX(0)" : "translateX(-100%)",
          transition:"transform 0.3s ease",
          display:"flex", flexDirection:"column",
          boxShadow: sidebarOpen ? "4px 0 20px rgba(0,0,0,0.3)" : "none",
        }}>
          {/* Close button inside sidebar */}
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"16px", borderBottom:`1px solid ${C.border}` }}>
            <div style={{ fontSize:15, fontWeight:800, color:C.accent }}>⚡ VolunteerHub</div>
            <button onClick={() => setSidebarOpen(false)}
              style={{ background:"none", border:"none", color:C.muted, fontSize:20, cursor:"pointer", padding:"4px 8px" }}>
              ✕
            </button>
          </div>

          <div style={{ flex:1, overflow:"hidden" }}>
            <Sidebar
              activePage={activePage === "eventDetail" ? "events" : activePage}
              onNavigate={handleNavigate}
              activeEvent={activeEvent}
              can={can}
            />
          </div>
          <UserBox user={user} logout={logout} />
        </div>

        {/* Main Content */}
        <div style={{ flex:1, overflow:"auto", padding:"20px 16px", background:C.bg, transition:"background 0.3s" }}>
          {renderPage()}
        </div>
      </div>
    );
  }

  // ── DESKTOP LAYOUT ────────────────────────
  return (
    <div className="app-layout">
      <div className="sidebar" style={{ borderRight:`1px solid ${C.border}` }}>
        <div style={{ flex:1, overflow:"hidden" }}>
          <Sidebar
            activePage={activePage === "eventDetail" ? "events" : activePage}
            onNavigate={handleNavigate}
            activeEvent={activeEvent}
            can={can}
          />
        </div>
        <UserBox user={user} logout={logout} />
      </div>
      <div className="main-content" style={{ background:C.bg, transition:"background 0.3s" }}>
        {renderPage()}
      </div>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <VolunteerDataProvider>
        <AuthProvider>
          <AppInner />
        </AuthProvider>
      </VolunteerDataProvider>
    </ThemeProvider>
  );
}

export default App;